import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatIndianCurrency } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const itemTypes = ['Job Work', 'Direct Supply'];
const jobWorkTypes = [
  'Threading', 'Hardening', 'Heat Treatment', 'Surface Grinding', 'Cylindrical Grinding', 'Polishing',
  'Plating', 'Powder Coating', 'Shot Blasting', 'Laser Cutting', 'Plasma Cutting', 'Forging', 'Blackening',
  'Painting', 'Welding', 'Machining', 'Assembly', 'Stamping', 'Plastic Moulding', 'Other'
];

export default function JobWorkVendors() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorItems, setVendorItems] = useState([]);
  const [partsMaster, setPartsMaster] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [usePartsMaster, setUsePartsMaster] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);

  const [vendorForm, setVendorForm] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_number: ''
  });

  const [itemForm, setItemForm] = useState({
    partcode: '',
    description: '',
    item_type: 'Job Work',
    job_work_type: '',
    rate_per_piece: '',
    unit: 'pieces',
    hsn_code: ''
  });

  useEffect(() => {
    fetchVendors();
    fetchPartsMaster();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/jobwork/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartsMaster = async () => {
    try {
      const response = await axios.get(`${API}/parts`);
      setPartsMaster(response.data);
    } catch (error) {
      console.error('Error fetching parts master:', error);
    }
  };

  const fetchVendorItems = async (vendorId) => {
    try {
      const response = await axios.get(`${API}/jobwork/vendor-items/${vendorId}`);
      setVendorItems(response.data);
    } catch (error) {
      console.error('Error fetching vendor items:', error);
    }
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode && editingVendorId) {
        await axios.put(`${API}/jobwork/vendors/${editingVendorId}`, vendorForm);
        if (selectedVendor?.id === editingVendorId) {
          setSelectedVendor({ ...selectedVendor, ...vendorForm });
        }
      } else {
        await axios.post(`${API}/jobwork/vendors`, vendorForm);
      }
      setShowVendorModal(false);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Error saving vendor: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditVendor = (vendor, e) => {
    e.stopPropagation();
    setIsEditMode(true);
    setEditingVendorId(vendor.id);
    setVendorForm({
      name: vendor.name || '',
      contact_person: vendor.contact_person || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      gst_number: vendor.gst_number || ''
    });
    setShowVendorModal(true);
  };

  const handleDeleteVendor = async (vendor, e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete vendor "${vendor.name}"? This will also delete all items associated with this vendor.`)) {
      try {
        await axios.delete(`${API}/jobwork/vendors/${vendor.id}`);
        if (selectedVendor?.id === vendor.id) {
          setSelectedVendor(null);
          setVendorItems([]);
        }
        fetchVendors();
      } catch (error) {
        console.error('Error deleting vendor:', error);
        alert('Error deleting vendor: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handlePartSelect = (e) => {
    const partcode = e.target.value;
    if (partcode) {
      const part = partsMaster.find(p => p.partcode === partcode);
      if (part) {
        setItemForm({
          ...itemForm,
          partcode: part.partcode,
          description: part.description,
          item_type: part.part_type === 'Outsourced' ? 'Job Work' : 'Direct Supply',
          unit: part.unit || 'pieces'
        });
      }
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    
    // Validate HSN code is provided
    if (!itemForm.hsn_code || itemForm.hsn_code.trim() === '') {
      alert('HSN/SAC Code is mandatory');
      return;
    }
    
    try {
      if (isEditingItem && editingItemId) {
        // Update existing item - don't send vendor_id
        await axios.put(`${API}/jobwork/vendor-items/${editingItemId}`, {
          partcode: itemForm.partcode,
          description: itemForm.description,
          item_type: itemForm.item_type,
          job_work_type: itemForm.job_work_type || null,
          rate_per_piece: itemForm.rate_per_piece ? parseFloat(itemForm.rate_per_piece) : null,
          unit: itemForm.unit,
          hsn_code: itemForm.hsn_code
        });
      } else {
        // Create new item - include vendor_id
        await axios.post(`${API}/jobwork/vendor-items`, {
          ...itemForm,
          vendor_id: selectedVendor.id,
          rate_per_piece: itemForm.rate_per_piece ? parseFloat(itemForm.rate_per_piece) : null
        });
      }
      setShowItemModal(false);
      resetItemForm();
      fetchVendorItems(selectedVendor.id);
      fetchPartsMaster(); // Refresh parts master in case new part was created
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditItem = (item) => {
    setIsEditingItem(true);
    setEditingItemId(item.id);
    setItemForm({
      partcode: item.partcode,
      description: item.description,
      item_type: item.item_type,
      job_work_type: item.job_work_type || '',
      rate_per_piece: item.rate_per_piece || '',
      unit: item.unit || 'pieces',
      hsn_code: item.hsn_code || ''
    });
    setUsePartsMaster(false);
    setShowItemModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`${API}/jobwork/vendor-items/${itemId}`);
        fetchVendorItems(selectedVendor.id);
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      gst_number: ''
    });
    setIsEditMode(false);
    setEditingVendorId(null);
  };

  const resetItemForm = () => {
    setItemForm({
      partcode: '',
      description: '',
      item_type: 'Job Work',
      job_work_type: '',
      rate_per_piece: '',
      unit: 'pieces',
      hsn_code: ''
    });
    setUsePartsMaster(false);
    setIsEditingItem(false);
    setEditingItemId(null);
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    fetchVendorItems(vendor.id);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="jobwork-vendors">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Job Work Vendors</h1>
        <button
          onClick={() => {
            resetVendorForm();
            setShowVendorModal(true);
          }}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition"
          data-testid="add-vendor-button"
        >
          + Add Vendor
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendors List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Vendors ({vendors.length})</h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {vendors.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No vendors added yet.
                </div>
              ) : (
                vendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    onClick={() => selectVendor(vendor)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedVendor?.id === vendor.id ? 'bg-red-50 border-l-4 border-red-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-gray-900">{vendor.name}</div>
                        <div className="text-sm text-gray-600 mt-1">{vendor.contact_person}</div>
                        <div className="text-xs text-gray-500 mt-1">{vendor.phone}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => handleEditVendor(vendor, e)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          title="Edit Vendor"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => handleDeleteVendor(vendor, e)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="Delete Vendor"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Vendor Details & Items */}
        <div className="lg:col-span-2">
          {selectedVendor ? (
            <div className="space-y-6">
              {/* Vendor Details Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">{selectedVendor.name}</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => handleEditVendor(selectedVendor, e)}
                      className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-600 rounded text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => handleDeleteVendor(selectedVendor, e)}
                      className="text-red-600 hover:text-red-800 px-3 py-1 border border-red-600 rounded text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Contact Person:</span>
                    <span className="ml-2 text-gray-900">{selectedVendor.contact_person || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <span className="ml-2 text-gray-900">{selectedVendor.phone || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 text-gray-900">{selectedVendor.email || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">GST Number:</span>
                    <span className="ml-2 text-gray-900">{selectedVendor.gst_number || '-'}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Address:</span>
                    <span className="ml-2 text-gray-900">{selectedVendor.address || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Item Catalog */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Item Catalog</h2>
                    <p className="text-sm text-gray-600 mt-1">Items this vendor supplies</p>
                  </div>
                  <button
                    onClick={() => setShowItemModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition text-sm"
                    data-testid="add-item-button"
                  >
                    + Add Item
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partcode</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HSN</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Work</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vendorItems.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                            No items added for this vendor yet.
                          </td>
                        </tr>
                      ) : (
                        vendorItems.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.partcode}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.description}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.hsn_code || '-'}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                item.item_type === 'Job Work'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {item.item_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.job_work_type || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {item.rate_per_piece ? formatIndianCurrency(item.rate_per_piece) : '-'}
                            </td>
                            <td className="px-6 py-4 text-sm space-x-2">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              <p className="text-lg">Select a vendor to view details and item catalog</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Vendor Modal */}
      {showVendorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {isEditMode ? 'Edit Vendor' : 'Add Job Work Vendor'}
              </h2>
            </div>
            <form onSubmit={handleVendorSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
                <input
                  type="text"
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={vendorForm.contact_person}
                    onChange={(e) => setVendorForm({ ...vendorForm, contact_person: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={vendorForm.phone}
                    onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={vendorForm.email}
                    onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    value={vendorForm.gst_number}
                    onChange={(e) => setVendorForm({ ...vendorForm, gst_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={vendorForm.address}
                  onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowVendorModal(false);
                    resetVendorForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  {isEditMode ? 'Save Changes' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Item Modal */}
      {showItemModal && selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {isEditingItem ? `Edit Item - ${itemForm.partcode}` : `Add Item to ${selectedVendor.name}`}
              </h2>
            </div>
            <form onSubmit={handleItemSubmit} className="p-6 space-y-4">
              {/* Toggle between Parts Master and Manual Entry (only for new items) */}
              {!isEditingItem && (
                <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!usePartsMaster}
                      onChange={() => setUsePartsMaster(false)}
                      className="w-4 h-4 text-red-600"
                    />
                    <span className="text-sm font-medium">Enter Manually</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={usePartsMaster}
                      onChange={() => setUsePartsMaster(true)}
                      className="w-4 h-4 text-red-600"
                    />
                    <span className="text-sm font-medium">Select from Parts Master ({partsMaster.length} parts)</span>
                  </label>
                </div>
              )}

              {/* Parts Master Selection */}
              {!isEditingItem && usePartsMaster && partsMaster.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Part from Master</label>
                  <select
                    onChange={handlePartSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- Select a part --</option>
                    {partsMaster.map(part => (
                      <option key={part.id} value={part.partcode}>
                        {part.partcode} - {part.description} ({part.part_type})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partcode *</label>
                  <input
                    type="text"
                    value={itemForm.partcode}
                    onChange={(e) => setItemForm({ ...itemForm, partcode: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    placeholder="e.g., PART-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Type *</label>
                  <select
                    value={itemForm.item_type}
                    onChange={(e) => setItemForm({ ...itemForm, item_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {itemTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              {itemForm.item_type === 'Job Work' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Work Type</label>
                  <select
                    value={itemForm.job_work_type}
                    onChange={(e) => setItemForm({ ...itemForm, job_work_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select type</option>
                    {jobWorkTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HSN/SAC Code *</label>
                  <input
                    type="text"
                    value={itemForm.hsn_code}
                    onChange={(e) => setItemForm({ ...itemForm, hsn_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 8708"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Piece (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.rate_per_piece}
                    onChange={(e) => setItemForm({ ...itemForm, rate_per_piece: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <input
                    type="text"
                    value={itemForm.unit}
                    onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Info about auto-sync */}
              {!isEditingItem && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  💡 <strong>Note:</strong> If this part does not exist in Parts Master, it will be automatically added.
                </div>
              )}

              {isEditingItem && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  💡 <strong>Note:</strong> Changing the rate will automatically record the price change in the part&apos;s price history.
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowItemModal(false);
                    resetItemForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  {isEditingItem ? 'Update Item' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
